import React from 'react';
import { Button, Modal } from 'antd';
import { MenuOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FuelReceipt } from '../../types';
import { formatVietnamCurrency } from '../../utils/numberFormatter';

interface FuelReceiptMobileColumnsProps {
  t: (key: string) => string;
  onEdit: (record: FuelReceipt) => void;
  onDelete: (id: number) => void;
  onUnlink: (id: number) => void;
  onStateTransition: (id: number, newState: 'WaitForProcess' | 'Created') => void;
  onViewImage: (id: number) => void;
}

export const getFuelReceiptMobileColumns = ({
  t,
  onEdit,
  onDelete,
  onUnlink,
  onStateTransition,
  onViewImage
}: FuelReceiptMobileColumnsProps): ColumnsType<FuelReceipt> => [
  {
    title: t('vehicle'),
    dataIndex: 'vehicleLicensePlate',
    key: 'vehicleLicensePlate',
    width: 100,
    fixed: 'left',
    render: (plate, record) => (
      <div>
        <strong style={{ fontSize: '12px' }}>{plate}</strong>
        <div style={{ fontSize: '10px', color: '#666' }}>
          {dayjs(record.refuelDate).format('DD/MM HH:mm')}
        </div>
      </div>
    )
  },
  {
    title: t('details'),
    key: 'details',
    render: (_, record) => (
      <div style={{ fontSize: '11px' }}>
        <div><strong>{record.liters}L</strong> × {formatVietnamCurrency(record.pricePerLiter)}</div>
        <div style={{ color: '#1890ff', fontWeight: 'bold' }}>
          = {formatVietnamCurrency(record.totalAmount)}
        </div>
        {record.gasStation && (
          <div style={{ color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
            {record.gasStation}
          </div>
        )}
      </div>
    )
  },
  {
    title: t('status'),
    key: 'status',
    width: 70,
    render: (_, record) => (
      <div style={{ textAlign: 'center' }}>
        <span style={{
          display: 'inline-block',
          padding: '2px 6px',
          borderRadius: '4px',
          backgroundColor: record.state === 'Created' ? '#52c41a' : '#faad14',
          color: 'white',
          fontSize: '10px'
        }}>
          {record.state === 'Created' ? '✓' : '○'}
        </span>
        {record.shippingRecordId && (
          <div style={{ fontSize: '9px', color: '#1890ff', marginTop: '2px' }}>
            Linked
          </div>
        )}
      </div>
    )
  },
  {
    title: '',
    key: 'actions',
    width: 50,
    fixed: 'right',
    render: (_, record) => (
      <Button
        icon={<MenuOutlined />}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          // Show action sheet for mobile
          Modal.confirm({
            title: record.vehicleLicensePlate,
            icon: null,
            content: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                <Button block onClick={() => { Modal.destroyAll(); onEdit(record); }}>
                  <EditOutlined /> {t('edit')}
                </Button>
                {record.state === 'WaitForProcess' ? (
                  <Button block type="primary" onClick={() => { Modal.destroyAll(); onStateTransition(record.id, 'Created'); }}>
                    {t('finalize') || 'Finalize'}
                  </Button>
                ) : (
                  <Button block onClick={() => { Modal.destroyAll(); onStateTransition(record.id, 'WaitForProcess'); }}>
                    {t('to.draft') || 'To Draft'}
                  </Button>
                )}
                {record.hasReceiptImage && (
                  <Button block onClick={() => { Modal.destroyAll(); onViewImage(record.id); }}>
                    {t('view.image') || 'View Image'}
                  </Button>
                )}
                {record.shippingRecordId && (
                  <Button block onClick={() => { Modal.destroyAll(); onUnlink(record.id); }}>
                    {t('unlink')}
                  </Button>
                )}
                <Button block danger onClick={() => { Modal.destroyAll(); onDelete(record.id); }}>
                  <DeleteOutlined /> {t('delete')}
                </Button>
              </div>
            ),
            okButtonProps: { style: { display: 'none' } },
            cancelText: t('close') || 'Close',
            width: '90%',
            centered: true
          });
        }}
      />
    )
  }
];
